
import {
    LocationModel,
    PanelDashboardModel,
    PanelDashboardLayoutTypeEnum,
    PanelDashboardSortTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import WorkTaskCreateDialog from '@/libs/work-tasks/components/WorkTaskCreateDialog.vue';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { panelDashboardsStore } from '@/libs/panel-dashboards/+state/store';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { NotificationScope } from '@/libs/common/model/NotificationScope';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import DeactivateQuickLoginDialog from '@/libs/common/components-smart/DeactivateQuickLoginDialog.vue';
import { AuthenticationType } from '@/libs/auth/models/auth-type';
import { PublicClientApplication } from '@azure/msal-browser';
import { GlobalVariables } from '@/libs/core/global';
import moment from 'moment-timezone';
import { ToastUtility } from '@syncfusion/ej2-notifications';
import show = ToastUtility.show;
import { getAppVersion } from '@/version';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import { computeOeeComponents } from '@/libs/tpm-analytics/core/oee.calculator';

@Component({
    methods: { show },

    computed: {
        ToastUtility() {
            return ToastUtility;
        },
    },
    components: {
        DeactivateQuickLoginDialog,
        WorkTaskCreateDialog,
    },
})
export default class PanelDashboard extends Page {
    loading = true;
    isLocked = false;
    unlockDialog = false;
    dateMenu = false;
    dateInput: string | null = null; // ISO bez sekund
    nowOverrideMs: number | null = null;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    @Prop() tenantId!: string;
    @Prop() panelId!: string;
    @Prop({ default: true }) showHeader!: boolean;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected items: { workplace: WorkplaceModel; issue: PlanItemModel | null }[] = [];
    private refreshInterval: any = null;
    private oeeByWorkplace: Record<string, number | null> = {};
    private availabilityByWorkplace: Record<string, number | null> = {};
    private performanceByWorkplace: Record<string, number | null> = {};
    private qualityByWorkplace: Record<string, number | null> = {};
    private aggregatedRawData: {
        totalOk: number;
        totalNok: number;
        totalXMinutes: number;
        totalUnplannedMinutes: number;
        totalIdealRuntimeMinutes: number;
    } = {
        totalOk: 0,
        totalNok: 0,
        totalXMinutes: 0,
        totalUnplannedMinutes: 0,
        totalIdealRuntimeMinutes: 0,
    };
    private aggregatedOeeComponents: {
        oee: number;
        availability: number;
        performance: number;
        quality: number;
    } = {
        oee: 0,
        availability: 0,
        performance: 0,
        quality: 0,
    };
    private targetsMap: Map<string | null, { worldClass?: number | null; oeeTarget?: number | null }> = new Map();
    private globalTargets: {
        oee: number | null;
        availability: number | null;
        performance: number | null;
        quality: number | null;
    } = {
        oee: null,
        availability: null,
        performance: null,
        quality: null,
    };
    private gaugeRenderKey = 0;
    private resizeTimeout: any = null;

    get panelDashboard(): PanelDashboardModel {
        return panelDashboardsStore.useGetter(CrudGetter.Detail);
    }

    get appVersion(): string {
        return getAppVersion();
    }

    get selectedDateLabel(): string {
        if (this.nowOverrideMs == null) return this.$t('common.today') as any;
        const d = new Date(this.nowOverrideMs);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${dd} ${hh}:${mm}`;
    }

    get avgOee(): number {
        return this.aggregatedOeeComponents.oee;
    }

    get avgAvailability(): number {
        return this.aggregatedOeeComponents.availability;
    }

    get avgPerformance(): number {
        return this.aggregatedOeeComponents.performance;
    }

    get avgQuality(): number {
        return this.aggregatedOeeComponents.quality;
    }

    get avgTargetOee(): number | null {
        const targets = Array.from(this.targetsMap.values())
            .map((t) => t?.oeeTarget ?? null)
            .filter((v): v is number => v != null);
        if (targets.length === 0) return null;
        const avg = targets.reduce((sum, v) => sum + v, 0) / targets.length;
        // Normalizace (může být 0-1 nebo 0-100)
        return avg > 1 ? avg / 100 : avg;
    }

    get gaugeHeight(): number {
        const width = this.$vuetify.breakpoint.width;
        const height = this.$vuetify.breakpoint.height;
        const withoutTable = !this.showTable;

        if (withoutTable) {
            // Maximální využití prostoru - odečteme jen nezbytné (header + minimální padding)
            const count = this.visibleGaugesCount;
            const headerHeight = this.showHeader ? 80 : 0;
            const minPadding = 40; // Minimální padding top/bottom
            const gapSize = width >= 1024 ? 32 : 20; // Gap mezi grafy

            if (count === 1) {
                // Jeden graf - využij skoro celou výšku obrazovky
                const availableHeight = height - headerHeight - minPadding;
                // Aspect ratio 1:1 nebo trochu širší, max 95% výšky
                return Math.min(availableHeight * 0.95, width * 0.85, 1000);
            } else if (count === 2) {
                // Dva grafy vedle sebe
                const availableHeight = height - headerHeight - minPadding;
                if (width >= 768) {
                    // Desktop/Tablet - vedle sebe, využij MAXIMUM výšky
                    return Math.min(availableHeight * 0.95, (width - gapSize - 60) / 2, 950);
                } else {
                    // Mobile - pod sebou
                    return Math.min((availableHeight - gapSize) * 0.48, width * 0.95, 550);
                }
            } else if (count === 3) {
                // Tři grafy - layout 2+1 (2 nahoře, 1 dole)
                const availableHeight = height - headerHeight - minPadding;
                if (width >= 768) {
                    // Desktop/Tablet - 2+1 layout, každý řádek má polovinu výšky
                    const rowHeight = (availableHeight - gapSize) / 2;
                    return Math.min(rowHeight, (width - gapSize - 60) / 2, 850);
                } else {
                    // Mobile - pod sebou
                    return Math.min((availableHeight - gapSize * 2) * 0.32, width * 0.95, 450);
                }
            } else {
                // Čtyři grafy - grid 2x2
                const availableHeight = height - headerHeight - minPadding;
                if (width >= 1024) {
                    // Desktop - 2x2 grid, využij MAXIMUM výšky
                    // Výška pro každý řádek = (celková dostupná výška - gap) / 2
                    const rowHeight = (availableHeight - gapSize) / 2;
                    return Math.min(rowHeight, (width - gapSize - 30) / 2, 850);
                } else if (width >= 768) {
                    // Tablet - 2x2
                    const rowHeight = (availableHeight - gapSize) / 2;
                    return Math.min(rowHeight, ((width - gapSize) / 2) * 1.1, 500);
                } else {
                    // Mobile - pod sebou
                    return Math.min((availableHeight - gapSize * 3) * 0.24, width * 0.95, 350);
                }
            }
        } else {
            // Standardní velikost s tabulkou - větší pro lepší viditelnost
            const count = this.visibleGaugesCount;

            if (width >= 2560) {
                // 4K+ monitory - maximální využití prostoru
                if (count === 1) return 600;
                if (count === 2) return 550;
                if (count === 3) return 500;
                return 450; // 4 grafy vedle sebe
            }
            if (width >= 1920) {
                // Full HD+ - velmi velké grafy
                if (count === 1) return 550;
                if (count === 2) return 500;
                if (count === 3) return 450;
                return 400; // 4 grafy vedle sebe
            }
            if (width >= 1800) {
                // Extra velké obrazovky - všechny grafy vedle sebe s volným prostorem
                if (count === 1) return 500;
                if (count === 2) return 450;
                if (count === 3) return 400;
                return 380; // 4 grafy vedle sebe
            }
            if (width >= 1600) {
                // Velké obrazovky - lepší proporce
                if (count === 1) return 480;
                if (count === 2) return 440;
                if (count === 3) return 400;
                return 370; // 4 grafy v 2x2
            }
            if (width >= 1440) {
                // Velké obrazovky 1440-1599px - 2x2 layout pro lepší využití prostoru
                if (count === 1) return 450;
                if (count === 2) return 420;
                if (count === 3) return 390;
                return 360; // 4 grafy v 2x2, větší než dříve
            }
            if (width >= 1320) {
                // Střední velké obrazovky
                if (count === 1) return 420;
                if (count === 2) return 400;
                if (count === 3) return 370;
                return 340; // 4 grafy v 2x2
            }
            if (width >= 1024) {
                // Desktop - standardní velikosti
                if (count === 1) return 380;
                if (count === 2) return 360;
                if (count === 3) return 330;
                return 310; // 4 grafy v 2x2
            }
            if (width >= 768) {
                // Tablet
                if (count <= 2) return 300;
                return 270;
            }
            // Mobile
            return 240;
        }
    }

    get showGauges(): boolean {
        const layoutType = this.panelDashboard?.layoutType;
        return layoutType === PanelDashboardLayoutTypeEnum.OnlyOee || layoutType === PanelDashboardLayoutTypeEnum.TableWithOee;
    }

    get showTable(): boolean {
        const layoutType = this.panelDashboard?.layoutType;
        return (
            layoutType === PanelDashboardLayoutTypeEnum.OnlyTable ||
            layoutType === PanelDashboardLayoutTypeEnum.TableWithOee ||
            layoutType == null // Zpětná kompatibilita pro staré záznamy
        );
    }

    get showOeeInTable(): boolean {
        return this.showTable && this.panelDashboard?.showOeeInTable === true;
    }

    get showOverallOeeGauge(): boolean {
        return this.showGauges && this.panelDashboard?.showOverallOee === true;
    }

    get showAvailabilityGauge(): boolean {
        return this.showGauges && this.panelDashboard?.showOeeAvailability === true;
    }

    get showPerformanceGauge(): boolean {
        return this.showGauges && this.panelDashboard?.showOeePerformance === true;
    }

    get showQualityGauge(): boolean {
        return this.showGauges && this.panelDashboard?.showOeeQuality === true;
    }

    get visibleGaugesCount(): number {
        let count = 0;
        if (this.showOverallOeeGauge) count++;
        if (this.showAvailabilityGauge) count++;
        if (this.showPerformanceGauge) count++;
        if (this.showQualityGauge) count++;
        return count;
    }

    get sortedItems(): { workplace: WorkplaceModel; issue: PlanItemModel | null }[] {
        if (!this.items) {
            return [];
        }
        const numericPriority = this.items.all(
            (x) => !x.issue?.auditFaultType?.priority || !isNaN(Number(x.issue?.auditFaultType?.priority))
        );
        const now = moment.tz(DISPLAY_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss');
        if (this.panelDashboard != null) {
            if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByWorkplace) {
                if (this.panelDashboard.sortAscending) {
                    return this.items
                        .orderBy(
                            (x) => x.workplace.name,
                            (a: any, b: any) => a.localeCompare(b!)
                        )
                        .thenBy(
                            (x) =>
                                numericPriority
                                    ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                    : x.issue?.auditFaultType?.priority,
                            (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                        )
                        .thenBy(
                            (x) =>
                                x.issue?.isWorkplaceShutdown
                                    ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                      new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                    : 2147483647 * 1000,
                            (a, b) => a - b
                        )
                        .toArray();
                } else {
                    return this.items
                        .orderByDescending(
                            (x) => x.workplace.name,
                            (a: any, b: any) => a.localeCompare(b!)
                        )
                        .thenByDescending(
                            (x) =>
                                numericPriority
                                    ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                    : x.issue?.auditFaultType?.priority,
                            (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                        )
                        .thenByDescending(
                            (x) =>
                                x.issue?.isWorkplaceShutdown
                                    ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                      new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                    : 2147483647 * 1000,
                            (a, b) => a - b
                        )
                        .toArray();
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByReportTime) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => b - a
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => b - a
                            )
                            .orderByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByShutdownDuration) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 2147483647 * 1000,
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 0,
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue.orderBy(
                            (x) => x.workplace.name ?? '',
                            (a, b) => a.localeCompare(b!)
                        ),
                    ];
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByFaultPriority) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) =>
                                    numericPriority
                                        ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                        : x.issue?.auditFaultType?.priority,
                                (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                            )
                            .thenBy(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 2147483647 * 1000,
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) =>
                                    numericPriority
                                        ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                        : x.issue?.auditFaultType?.priority,
                                (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                            )
                            .thenByDescending(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 0,
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue.orderBy(
                            (x) => x.workplace.name ?? '',
                            (a, b) => a.localeCompare(b!)
                        ),
                    ];
                }
            }
        }
        return this.items;
    }

    async mounted() {
        authStore.dispatch(AuthAction.SetTvMode, true);
        this.isLocked = authStore.useGetter(AuthGetter.GetIsLocked) ?? false;
        await this.loadPanel();
        this.refreshInterval = setInterval(() => {
            this.loadPanel(true);
        }, 10000);
        this.autoScrollPanelDashboardTable();
        window.addEventListener('resize', this.handleResize);
    }

    async destroyed() {
        authStore.dispatch(AuthAction.SetTvMode, false);
        clearInterval(this.refreshInterval);
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('panelId')
    async panelIdChanged() {
        await this.loadPanel();
    }

    @Watch('showTable')
    onShowTableChanged() {
        // Přegeneruj grafy při změně zobrazení tabulky
        this.$nextTick(() => {
            this.gaugeRenderKey++;
        });
    }

    handleResize() {
        // Vyčistíme předchozí timeout
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        // Nastavíme nový timeout s debounce 300ms
        this.resizeTimeout = setTimeout(() => {
            // Zvýšíme klíč pro vynucení re-renderu gauge grafů
            this.gaugeRenderKey++;
        }, 300);
    }

    async loadPanel(silent = false) {
        if (!silent) {
            this.loading = true;
        }
        panelDashboardsStore.dispatch(CrudAction.Get, { id: this.panelId });
        const panelDashboard = await this.subscribe(panelDashboardsStore, CrudReponse.Get);
        if (panelDashboard.language != this.$i18n.locale) {
            this.$i18n.locale = panelDashboard.language;
            window.localStorage.language = panelDashboard.language;
            this.$vuetify.lang.current = panelDashboard.language;
            GlobalVariables.lang = panelDashboard.language;
            moment.locale(this.$vuetify.lang.current);
        }
        await this.loadItemsGraphQL();
        await this.loadOeeForCurrentShift();
        if (!silent) {
            this.loading = false;
        }
    }

    async loadItems() {
        const referenceDateTime =
            this.nowOverrideMs != null
                ? moment.tz(this.nowOverrideMs, DISPLAY_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss')
                : moment.tz(DISPLAY_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss');

        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'time', index: 1, order: 'desc' },
                [
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.IssueSolving },
                    { field: 'time', op: 'lte', comparand: referenceDateTime },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'planItemState',
                                op: 'eq',
                                comparand: 'WaitingToPerform',
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'isWorkplaceShutdown',
                                        op: 'eq',
                                        comparand: true,
                                    },
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lte',
                                        comparand: referenceDateTime,
                                    },
                                    {
                                        field: 'id',
                                        op: 'wrap',
                                        comparand: [
                                            {
                                                field: 'shutdownToDate',
                                                op: 'eq',
                                                comparand: null,
                                                junction: 'or',
                                            },
                                            {
                                                field: 'shutdownToDate',
                                                op: 'gte',
                                                comparand: referenceDateTime,
                                                junction: 'or',
                                            },
                                        ],
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 999,
                },
                false,
                'PanelDashboard',
                true
            )
        );
        await this.subscribe(planItemsStore, CrudReponse.GetAllBasic, 'PanelDashboard');
        const panelDashboard = panelDashboardsStore.useGetter(CrudGetter.Detail);
        const notificationScope: NotificationScope = panelDashboard?.locationScope
            ? JSON.parse(panelDashboard?.locationScope)
            : null;
        let items = [...planItemsStore.useGetter(CrudGetter.DataBasic, 'PanelDashboard')] as PlanItemModel[];
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeWorkplaces = notificationScope?.workplaces ?? [];
        const activeLocations = notificationScope?.locations ?? [];
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
            .map((x) => x.id!)
            .concat(activeLocations!);
        if (activeWorkplaces.length > 0) {
            items = items.filter((x) => activeWorkplaces.includes(x.workplaceId!));
            workplaces = workplaces.filter((x) => activeWorkplaces.includes(x.id!));
        } else if (activeLocations.length > 0) {
            const workplacesToFilter = workplaces.filter((x) => locationScopes.includes(x.locationId!)).map((x) => x.id!);
            items = items.filter((x) => workplacesToFilter.includes(x.workplaceId!));
            workplaces = workplaces.filter((x) => workplacesToFilter.includes(x.id!));
        }
        workplaces = workplaces.sort((a, b) => a.name!.localeCompare(b.name!));
        const output: { workplace: WorkplaceModel; issue: PlanItemModel | null }[] = [];
        for (const workplace of workplaces) {
            const filteredItems = items
                .filter((x) => x.workplaceId == workplace.id)
                .sort((a, b) => new Date(a.time!).getTime() - new Date(b.time!).getTime());
            if (filteredItems.length == 0) {
                output.push({
                    workplace,
                    issue: null,
                });
            } else if (filteredItems.length == 0) {
                output.push({
                    workplace,
                    issue: filteredItems[0],
                });
            } else {
                for (const item of filteredItems) {
                    output.push({
                        workplace,
                        issue: item,
                    });
                }
            }
        }
        this.items = output;
    }

    /**
     * Nová metoda pro načítání planItems přes GraphQL
     * Alternativa k loadItems() - neovlivňuje původní metodu
     */
    async loadItemsGraphQL() {
        const referenceDateTime =
            this.nowOverrideMs != null
                ? moment.tz(this.nowOverrideMs, DISPLAY_TIMEZONE).toISOString()
                : moment.tz(DISPLAY_TIMEZONE).toISOString();

        const panelDashboard = panelDashboardsStore.useGetter(CrudGetter.Detail);
        const notificationScope: NotificationScope = panelDashboard?.locationScope
            ? JSON.parse(panelDashboard?.locationScope)
            : null;
        
        const activeWorkplaces = notificationScope?.workplaces ?? [];
        const activeLocations = notificationScope?.locations ?? [];

        // Příprava filtrů pro GraphQL dotaz
        const filters: { workplaceIds?: string[]; locationIds?: string[] } = {};
        if (activeWorkplaces.length > 0) {
            filters.workplaceIds = activeWorkplaces;
        } else if (activeLocations.length > 0) {
            // Pro locationIds musíme zahrnout i podřízené lokace
            const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
            const locationScopes = locations
                .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
                .map((x) => x.id!)
                .concat(activeLocations!);
            filters.locationIds = locationScopes;
        }

        // Načtení planItems přes GraphQL
        const { PlanItemsRepository } = await import('@/libs/tpm-analytics/data/plan-items.repository');
        let items = await PlanItemsRepository.fetchPanelDashboardPlanItems(referenceDateTime, filters);

        // Sestavení displayName z firstName a lastName pro kompatibilitu
        items = items.map((item) => {
            const mappedItem = { ...item };
            if (mappedItem.reportedBy && mappedItem.reportedBy.user) {
                const user = mappedItem.reportedBy.user as any;
                if (!user.displayName && (user.firstName || user.lastName)) {
                    user.displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;
                }
            }
            if (mappedItem.tenantUser && mappedItem.tenantUser.user) {
                const user = mappedItem.tenantUser.user as any;
                if (!user.displayName && (user.firstName || user.lastName)) {
                    user.displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;
                }
            }
            return mappedItem;
        });

        // Načtení workplaces
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        
        // Další filtrování workplaces dle notificationScope
        if (activeWorkplaces.length > 0) {
            workplaces = workplaces.filter((x) => activeWorkplaces.includes(x.id!));
        } else if (activeLocations.length > 0) {
            const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
            const locationScopes = locations
                .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
                .map((x) => x.id!)
                .concat(activeLocations!);
            workplaces = workplaces.filter((x) => locationScopes.includes(x.locationId!));
        }

        // Seřazení workplaces dle názvu
        workplaces = workplaces.sort((a, b) => a.name!.localeCompare(b.name!));

        // Sestavení výstupní struktury: pro každé pracoviště najdeme odpovídající planItems
        const output: { workplace: WorkplaceModel; issue: PlanItemModel | null }[] = [];
        for (const workplace of workplaces) {
            const filteredItems = items
                .filter((x) => x.workplaceId == workplace.id)
                .sort((a, b) => new Date(a.time!).getTime() - new Date(b.time!).getTime());
            
            if (filteredItems.length == 0) {
                output.push({
                    workplace,
                    issue: null,
                });
            } else {
                for (const item of filteredItems) {
                    output.push({
                        workplace,
                        issue: item,
                    });
                }
            }
        }
        
        this.items = output;
    }

    async loadOeeForCurrentShift() {
        try {
            const panelDashboard = panelDashboardsStore.useGetter(CrudGetter.Detail);
            const notificationScope: NotificationScope = panelDashboard?.locationScope
                ? JSON.parse(panelDashboard?.locationScope)
                : null;
            const activeWorkplaces = notificationScope?.workplaces ?? [];
            const activeLocations = notificationScope?.locations ?? [];
            const filters: { locationIds?: string[]; workplaceIds?: string[] } = {};
            if (activeWorkplaces.length > 0) filters.workplaceIds = activeWorkplaces;
            else if (activeLocations.length > 0) filters.locationIds = activeLocations;

            const { getPanelCurrentOeeByWorkplace } = await import('@/libs/tpm-analytics/services/oee/panel-current.service');
            const opts = this.nowOverrideMs != null ? { nowMs: this.nowOverrideMs as number } : undefined;
            const map = await getPanelCurrentOeeByWorkplace(filters, opts as any);
            const nextOee: Record<string, number | null> = {};
            const nextA: Record<string, number | null> = {};
            const nextE: Record<string, number | null> = {};
            const nextQ: Record<string, number | null> = {};
            
            // Agregace surových dat pro správný výpočet celkového OEE
            let totalOk = 0;
            let totalNok = 0;
            let totalXMinutes = 0;
            let totalUnplannedMinutes = 0;
            let totalIdealRuntimeMinutes = 0;
            
            for (const [wid, comp] of map.entries()) {
                nextOee[wid || ''] = comp?.oee ?? null;
                nextA[wid || ''] = comp?.availability ?? null;
                nextE[wid || ''] = comp?.performance ?? null;
                nextQ[wid || ''] = comp?.quality ?? null;
                
                // Akumulace surových dat
                totalOk += comp?.totalOk ?? 0;
                totalNok += comp?.totalNok ?? 0;
                totalXMinutes += comp?.totalXMinutes ?? 0;
                totalUnplannedMinutes += comp?.totalUnplannedMinutes ?? 0;
                totalIdealRuntimeMinutes += comp?.totalIdealRuntimeMinutes ?? 0;
            }
            
            this.oeeByWorkplace = nextOee;
            this.availabilityByWorkplace = nextA;
            this.performanceByWorkplace = nextE;
            this.qualityByWorkplace = nextQ;
            
            // Uložení agregovaných surových dat
            this.aggregatedRawData = {
                totalOk,
                totalNok,
                totalXMinutes,
                totalUnplannedMinutes,
                totalIdealRuntimeMinutes,
            };

            // Výpočet celkových OEE komponent z agregovaných dat (jednou, ne 4x v getterech)
            const aggregatedComp = computeOeeComponents(
                totalOk,
                totalNok,
                0, // planCount
                totalXMinutes,
                totalUnplannedMinutes,
                totalIdealRuntimeMinutes
            );

            this.aggregatedOeeComponents = {
                oee: aggregatedComp.oee ?? 0,
                availability: aggregatedComp.availability ?? 0,
                performance: aggregatedComp.performance ?? 0,
                quality: aggregatedComp.quality ?? 0,
            };

            // Načtení targetů OEE pro pracoviště
            try {
                const referenceDateTime =
                    this.nowOverrideMs != null
                        ? moment.tz(this.nowOverrideMs, DISPLAY_TIMEZONE).toISOString()
                        : moment.tz(DISPLAY_TIMEZONE).toISOString();
                const { getTargetsPerWorkplace, getGlobalTargets } = await import(
                    '@/libs/tpm-analytics/services/oee/targets.service'
                );
                const workplacesData = this.items.map((item) => ({
                    workplaceId: item.workplace.id ?? null,
                    locationId: item.workplace.locationId ?? null,
                }));
                this.targetsMap = await getTargetsPerWorkplace(referenceDateTime, referenceDateTime, workplacesData);
                this.globalTargets = await getGlobalTargets(referenceDateTime, referenceDateTime);
            } catch {
                // V případě chyby ponecháme prázdnou mapu
            }
        } catch (err) {
            // swallow errors in TV mode
            // console.warn('[PanelDashboard] OEE load failed', err);
        }
    }

    formatOeePercent(value: number | null | undefined): string {
        if (value == null || !isFinite(value as number)) return '--';
        return `${Math.round((value as number) * 100)} %`;
    }

    getGaugeColor(value: number, metricType: 'oee' | 'availability' | 'performance' | 'quality'): string {
        if (!isFinite(value)) return '#9e9e9e';

        const valuePct = value * 100;

        // Pro celkové OEE: > 85% (zelená), > 70% (žlutá), jinak červená
        if (metricType === 'oee') {
            if (valuePct > 85) return '#4caf50'; // Zelená
            if (valuePct > 70) return '#ffc107'; // Žlutá
            return '#f44336'; // Červená
        }

        // Pro jednotlivé složky: > 90% (zelená), > 75% (žlutá), jinak červená
        if (valuePct > 90) return '#4caf50'; // Zelená
        if (valuePct > 75) return '#ffc107'; // Žlutá
        return '#f44336'; // Červená
    }

    getGaugeOptions(label: string, color: string) {
        const height = this.gaugeHeight;
        let nameFontSize = '28px';
        let valueFontSize = '64px';
        let valueOffsetY = -60;
        let nameOffsetY = 5;

        // Výpočet velikostí fontů na základě výšky grafu
        // nameFontSize: cca 8-9% výšky grafu
        // valueFontSize: cca 18-20% výšky grafu
        const nameSize = Math.round(height * 0.085);
        const valueSize = Math.round(height * 0.19);
        
        nameFontSize = `${nameSize}px`;
        valueFontSize = `${valueSize}px`;
        
        // Offsety proporcionální k výšce
        valueOffsetY = Math.round(height * -0.18);
        nameOffsetY = Math.round(height * 0.015);
        
        // Minimální a maximální limity pro lepší čitelnost
        const nameMin = 14;
        const nameMax = 56;
        const valueMin = 28;
        const valueMax = 110;
        
        const nameSizeNum = Math.max(nameMin, Math.min(nameMax, nameSize));
        const valueSizeNum = Math.max(valueMin, Math.min(valueMax, valueSize));
        
        nameFontSize = `${nameSizeNum}px`;
        valueFontSize = `${valueSizeNum}px`;
        
        // Ujisti se, že offsety nejsou příliš extrémní
        valueOffsetY = Math.max(-95, Math.min(-30, valueOffsetY));
        nameOffsetY = Math.max(0, Math.min(20, nameOffsetY));

        return {
            chart: {
                type: 'radialBar',
                sparkline: {
                    enabled: true,
                },
            },
            plotOptions: {
                radialBar: {
                    startAngle: -90,
                    endAngle: 90,
                    hollow: {
                        margin: 0,
                        size: '68%',
                        background: 'transparent',
                    },
                    track: {
                        background: '#e0e0e0',
                        strokeWidth: '100%',
                        margin: 10,
                    },
                    dataLabels: {
                        name: {
                            show: true,
                            fontSize: nameFontSize,
                            fontWeight: 700,
                            offsetY: nameOffsetY,
                            color: '#333',
                        },
                        value: {
                            offsetY: valueOffsetY,
                            fontSize: valueFontSize,
                            fontWeight: 800,
                            color: color,
                            formatter: (val: number) => `${val}%`,
                        },
                    },
                },
            },
            fill: {
                colors: [color],
            },
            stroke: {
                lineCap: 'round',
            },
            labels: [label],
        };
    }

    getOeeCellBackgroundColor(workplaceId: string | null, metricType: 'oee' | 'availability' | 'performance' | 'quality' = 'oee'): string {
        // Získáme hodnotu metriky podle typu
        let metricValue: number | null = null;
        switch (metricType) {
            case 'oee':
                metricValue = this.oeeByWorkplace[workplaceId || ''];
                break;
            case 'availability':
                metricValue = this.availabilityByWorkplace[workplaceId || ''];
                break;
            case 'performance':
                metricValue = this.performanceByWorkplace[workplaceId || ''];
                break;
            case 'quality':
                metricValue = this.qualityByWorkplace[workplaceId || ''];
                break;
        }

        if (metricValue == null || !isFinite(metricValue)) return 'transparent';

        const valuePct = metricValue * 100;

        // Pro celkové OEE: > 85% (zelená), > 70% (žlutá), jinak červená
        if (metricType === 'oee') {
            if (valuePct > 85) return '#4caf50'; // Zelená
            if (valuePct > 70) return '#ffc107'; // Žlutá
            return '#f44336'; // Červená
        }

        // Pro jednotlivé složky: > 90% (zelená), > 75% (žlutá), jinak červená
        if (valuePct > 90) return '#4caf50'; // Zelená
        if (valuePct > 75) return '#ffc107'; // Žlutá
        return '#f44336'; // Červená
    }

    getOeeCellFontColor(workplaceId: string | null, metricType: 'oee' | 'availability' | 'performance' | 'quality' = 'oee'): string {
        const backgroundColor = this.getOeeCellBackgroundColor(workplaceId, metricType).replace('#', '');
        if (backgroundColor === 'transparent') return 'inherit';
        return this.useWhiteFont(backgroundColor) ? '#fff' : '#000';
    }

    getBackgroundColor(planItem: PlanItemModel) {
        return `#${planItem?.auditFaultType?.color ?? '65bf0a'}`;
    }

    getFontColor(planItem: PlanItemModel): string {
        const backgroundColor = this.getBackgroundColor(planItem).replace('#', '');
        return this.useWhiteFont(backgroundColor) ? '#fff' : '#000';
    }

    lock() {
        this.isLocked = true;
        authStore.dispatch(AuthAction.SetIsLocked, true);
    }

    unlock() {
        this.unlockDialog = true;
    }

    private signout() {
        authStore.dispatch(AuthAction.SignOut);
        const authenticationType = authStore.useGetter(AuthGetter.GetAuthenticationType) as AuthenticationType;
        if (authenticationType == AuthenticationType.AzureActiveDirectory) {
            const azureTenantId = authStore.useGetter(AuthGetter.GetAzureTenantId);
            const azureAppId = authStore.useGetter(AuthGetter.GetAzureAppId);
            const msalConfig = {
                auth: {
                    clientId: azureAppId,
                    authority: 'https://login.microsoftonline.com/' + azureTenantId,
                },
            };
            const msalInstance = new PublicClientApplication(msalConfig);
            msalInstance.handleRedirectPromise();
            msalInstance.logoutPopup();
        }
    }

    applyCustomDate() {
        if (!this.dateInput) return;
        const parsed = moment.tz(this.dateInput as string, 'YYYY-MM-DDTHH:mm:ss', DISPLAY_TIMEZONE);
        if (!parsed.isValid()) return;
        this.nowOverrideMs = parsed.valueOf();
        this.dateMenu = false;
        this.loadPanel(true);
    }

    setNow() {
        this.nowOverrideMs = null;
        const d = moment.tz(DISPLAY_TIMEZONE);
        const y = d.year();
        const m = String(d.month() + 1).padStart(2, '0');
        const dd = String(d.date()).padStart(2, '0');
        const hh = String(d.hours()).padStart(2, '0');
        const mm = String(d.minutes()).padStart(2, '0');
        this.dateInput = `${y}-${m}-${dd}T${hh}:${mm}:00`;
        this.dateMenu = false;
        this.loadPanel(true);
    }

    autoScrollPanelDashboardTable() {
        const tableElement = document.querySelector('.panel-dashboard-table');

        if (!tableElement) {
            // Tabulka není zobrazena (např. layout OnlyCharts), scrollování se nespustí
            return;
        }

        const table = tableElement.parentNode as HTMLElement;

        if (!table) {
            console.warn("Element '.panel-dashboard-table' nemá parentNode.");
            return;
        }

        let direction: 'down' | 'up' = 'down';
        let paused = false;
        let autoscroll = false;

        const scroll = () => {
            if (paused) return; // Pokud je pozastaveno, přeskočí scrollování

            const maxScrollTop = table.scrollHeight - table.clientHeight;

            if (direction === 'down') {
                autoscroll = true;
                table.scrollTop += 1; // Nastavte hodnotu podle požadované rychlosti
                if (table.scrollTop + 1 >= maxScrollTop) {
                    direction = 'up';
                    setTimeout(scroll, 10000); // Po dosažení dolního okraje čeká 3 sekundy
                    return;
                }
            } else {
                autoscroll = true;
                table.scrollTop -= 1; // Posun nahoru
                if (table.scrollTop - 1 <= 0) {
                    direction = 'down';
                    setTimeout(scroll, 10000); // Po dosažení horního okraje čeká 3 sekundy
                    return;
                }
            }
            requestAnimationFrame(scroll); // Nepřetržitý posun
        };
        let timeout: any = null;

        const pauseScrolling = () => {
            if (autoscroll) {
                autoscroll = false;
                return;
            }
            paused = true;
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                paused = false; // Obnoví scrollování po 5 sekundách
                scroll();
            }, 3000);
        };

        // Posluchače událostí pro interakci uživatele
        document.addEventListener('scroll', pauseScrolling, true);
        document.addEventListener('mousemove', pauseScrolling, true);
        document.addEventListener('click', pauseScrolling, true);
        document.addEventListener('keydown', pauseScrolling, true);

        // Spustí automatické scrollování
        timeout = setTimeout(() => {
            scroll();
        }, 3000);
    }
}
